/**
 * A filled-in FAQ spreadsheet → the markdown the knowledge base stores.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * Nobody writes a casino's FAQ in markdown. They write it in the sheet we hand
 * them — TOPIC / WHAT TO ANSWER / YOUR ANSWER — pass it round the CS team, and
 * send it back with sixty rows filled in. Asking them to retype that as `## `
 * headings is asking them to do a conversion a computer should do, and to make
 * a typo in it.
 *
 * ── The rules it follows are the SHEET'S own rules ───────────────────────
 * The template's "HOW TO FILL" page says: blank means the bot passes the player
 * to a human, and blank is always better than guessing. So a row with no answer
 * is SKIPPED, not imported as an empty section — importing it would publish a
 * heading the bot can match with nothing behind it, which is the one outcome
 * the instruction sheet exists to prevent.
 *
 * The EXAMPLE column is marked "for reference only" on the sheet. It is sample
 * text, not this client's answer, so it never reaches the bot.
 */

/**
 * Headers are matched by TEXT, not by position: somebody will reorder the
 * columns or paste the table into a new sheet, and a positional parser would
 * silently import the wrong column as the answer.
 *
 * ── Specific before loose, and never twice ──────────────────────────────
 * The template's two middle headers are "WHAT TO ANSWER" and "YOUR ANSWER".
 * Both contain the word "answer", so a loose match on "answer" finds the
 * QUESTION column first and imports every question as its own answer — which
 * is exactly what the first version of this did, and it looks plausible enough
 * in a spot check to ship. Each key therefore tries its exact header first,
 * loose words only as a fallback, and a column already claimed is off the
 * table.
 */
const COLUMNS = {
  answer: [['your answer'], ['answer', 'jawapan', '答案']],
  question: [['what to answer'], ['question', 'soalan', '问题']],
  topic: [['topic'], ['category', 'kategori', '主题', '分类']],
}

function norm(v) {
  return String(v == null ? '' : v)
    .replace(/\s+/g, ' ')
    .trim()
}

/** The header row is not always row 1 — a title or a blank row above it is
 *  normal. Find the first row that carries the answer column. */
function findHeader(rows) {
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const cells = (rows[i] || []).map((c) => norm(c).toLowerCase())
    const map = {}
    const taken = new Set()

    // Two passes: every key gets its exact header before any key is allowed a
    // loose one. Answer is resolved first because it is the column that must
    // not be wrong.
    for (const pass of [0, 1]) {
      for (const [key, tiers] of Object.entries(COLUMNS)) {
        if (map[key] !== undefined) continue
        const words = tiers[pass] || []
        const idx = cells.findIndex((c, ci) => !taken.has(ci) && words.some((w) => c.includes(w)))
        if (idx >= 0) {
          map[key] = idx
          taken.add(idx)
        }
      }
    }

    if (map.question !== undefined && map.answer !== undefined) return { row: i, map }
  }
  return null
}

/**
 * @param {Array<Array<any>>} rows  a sheet as an array of arrays
 * @returns {{markdown: string, imported: number, skipped: Array<string>, topics: string[]}}
 */
export function sheetToMarkdown(rows) {
  const header = findHeader(rows)
  if (!header) {
    throw new Error(
      'Could not find the "WHAT TO ANSWER" and "YOUR ANSWER" columns. Is this the FAQ sheet we sent?',
    )
  }

  const { map } = header
  const out = []
  const skipped = []
  const topics = []
  // TOPIC is written once per group and left blank on the rows under it, the
  // way anybody fills a sheet by hand. Carry it down.
  let topic = ''

  for (let i = header.row + 1; i < rows.length; i++) {
    const row = rows[i] || []
    const t = map.topic !== undefined ? norm(row[map.topic]) : ''
    const q = norm(row[map.question])
    const a = String(row[map.answer] == null ? '' : row[map.answer]).trim()

    if (t) {
      topic = t
      if (!topics.includes(t)) topics.push(t)
    }
    if (!q) continue

    if (!a) {
      // The sheet's own rule: blank is a deliberate "let a human take this".
      skipped.push(topic ? `${topic} — ${q}` : q)
      continue
    }

    // Topic in the heading, because the heading is what the bot matches a
    // player's question against: "Minimum amount" on its own is ambiguous
    // between deposit and withdrawal, and that ambiguity is a wrong answer.
    out.push(`## ${topic ? `${topic} — ${q}` : q}\n${a}`)
  }

  return { markdown: out.join('\n\n'), imported: out.length, skipped, topics }
}

/**
 * Read a workbook or CSV into rows. SheetJS is imported lazily — it is ~400 KB
 * and only an operator importing a sheet ever needs it.
 */
export async function readSheet(file) {
  const XLSX = await import('xlsx')
  const data = await file.arrayBuffer()
  const wb = XLSX.read(data, { type: 'array' })

  // The template has an instructions sheet first, so "the first sheet" is the
  // wrong guess. Prefer one that actually parses; name it if we can.
  const named = wb.SheetNames.find((n) => /faq/i.test(n))
  const order = named ? [named, ...wb.SheetNames.filter((n) => n !== named)] : wb.SheetNames

  let lastError = null
  for (const name of order) {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false, defval: '' })
    try {
      const result = sheetToMarkdown(rows)
      if (result.imported || result.skipped.length) return { ...result, sheet: name }
    } catch (e) {
      lastError = e
    }
  }
  throw lastError || new Error('No FAQ rows found in that file.')
}
